import baseGoApi from "@/api/baseGoApi";
import { Service, WeeklySchedule } from "@/types/Service";

const SERVICE_ENDPOINT = "/services";

/**
 * Fetch a list of all services.
 * @param includeDeleted When true, soft-deleted lesson types are included
 *   (each carries a non-null `deletedAt`). Used by the booking edit form so a
 *   booking whose lesson type was removed can still display it.
 * @returns Promise<Service[]>
 */
export const fetchServices = async (includeDeleted = false): Promise<Service[]> => {
    try {
        const response = await baseGoApi.get(`${SERVICE_ENDPOINT}/`, {
            params: includeDeleted ? { includeDeleted: "true" } : undefined,
        });
        return response.data as Service[];
    } catch (error: any) {
        console.error("Error fetching services:", error);
        throw new Error("Failed to fetch services.");
    }
};

/**
 * Fetch a single service by ID.
 * @param id Service ID
 * @returns Promise<Service>
 */
export const fetchServiceById = async (id: number): Promise<Service> => {
    try {
        const response = await baseGoApi.get(`${SERVICE_ENDPOINT}/${id}`);
        return response.data as Service;
    } catch (error: any) {
        console.error(`Error fetching service with ID ${id}:`, error);
        throw new Error(`Failed to fetch service with ID ${id}.`);
    }
};

/**
 * Create a new service.
 * @param serviceData Data for the new service
 * @returns Promise<Service>
 */
// Accept backend DTO shape for create/update
type IdRef = { id: number };
export type ServiceUpdateDto = Partial<Omit<Service,
    | 'equipments'
    | 'users'
    | 'organisation'
    | 'createdAt'
    | 'updatedAt'
    | 'deletedAt'
>> & {
    equipments?: IdRef[];
    users?: IdRef[];
    weeklySchedule?: WeeklySchedule;
};

export const createService = async (serviceData: ServiceUpdateDto): Promise<Service> => {
    try {
        const response = await baseGoApi.post(SERVICE_ENDPOINT + '/', serviceData);
        return response.data as Service;
    } catch (error: any) {
        console.error("Error creating service:", error);
        throw new Error("Failed to create service.");
    }
};

/**
 * Update an existing service.
 * @param id Service ID
 * @param serviceData Updated service data
 * @returns Promise<Service>
 */
export const updateService = async (id: number, serviceData: ServiceUpdateDto): Promise<Service> => {
    try {
        const response = await baseGoApi.put(`${SERVICE_ENDPOINT}/${id}`, serviceData);
        return response.data as Service;
    } catch (error: any) {
        console.error(`Error updating service with ID ${id}:`, error);
        throw new Error(`Failed to update service with ID ${id}.`);
    }
};

/**
 * Delete a service by ID.
 * @param id Service ID
 * @returns Promise<void>
 */
export const deleteService = async (id: number): Promise<void> => {
    try {
        await baseGoApi.delete(`${SERVICE_ENDPOINT}/${id}`);
    } catch (error: any) {
        console.error(`Error deleting service with ID ${id}:`, error);
        throw new Error(`Failed to delete service with ID ${id}.`);
    }
};

/**
 * Send sorted IDs to the server to update the order in the database.
 * @param sortedIDs array of service IDs in new order
 */
export const sortServices = async (sortedIDs: number[]): Promise<void> => {
    try {
        await baseGoApi.post("/services/sort", { order: sortedIDs });
    } catch (error: any) {
        console.error("Error saving sorted service order:", error);
        throw new Error("Failed to save service order.");
    }
};